export function PropertyDetailInternalSkeleton() {
  return (
    <div className="min-h-screen bg-white px-5 py-12 dark:bg-black">
      <div className="mx-auto max-w-[1300px] space-y-6">
        {/* Topbar skeleton */}
        <div className="h-16 w-full rounded-2xl skeleton-shimmer" />

        {/* Tabs de navegación skeleton — 6 tabs */}
        <div className="flex items-center gap-8 border-b border-slate-100 pb-4 dark:border-zinc-900">
          {["w-20", "w-12", "w-24", "w-16", "w-14", "w-20"].map((w, i) => (
            <div key={i} className={`h-4 ${w} rounded skeleton-shimmer`} />
          ))}
        </div>

        <div className="grid gap-8 md:grid-cols-[380px_minmax(0,1fr)]">
          {/* ── Columna izquierda ── */}
          <div className="space-y-6">
            {/* Badges primarios: property_type, status, publication */}
            <div className="flex flex-wrap gap-2">
              <div className="h-6 w-20 rounded-full skeleton-shimmer" />
              <div className="h-6 w-24 rounded-full skeleton-shimmer" />
              <div className="h-6 w-16 rounded-full skeleton-shimmer" />
            </div>

            {/* Título */}
            <div className="space-y-2">
              <div className="h-9 w-3/4 rounded skeleton-shimmer" />
              <div className="h-9 w-1/2 rounded skeleton-shimmer" />
            </div>

            {/* Ubicación */}
            <div className="h-4 w-1/2 rounded skeleton-shimmer" />

            {/* Precio */}
            <div className="flex items-baseline gap-2">
              <div className="h-8 w-1/3 rounded skeleton-shimmer" />
              <div className="h-5 w-10 rounded skeleton-shimmer" />
            </div>

            {/* Badges secundarios: mascotas, condición, disponibilidad, vistas, favoritos */}
            <div className="flex flex-wrap gap-2.5">
              <div className="h-6 w-28 rounded-full skeleton-shimmer" />
              <div className="h-6 w-32 rounded-full skeleton-shimmer" />
              <div className="h-6 w-36 rounded-full skeleton-shimmer" />
              <div className="h-6 w-24 rounded-full skeleton-shimmer" />
              <div className="h-6 w-24 rounded-full skeleton-shimmer" />
            </div>

            {/* Featured badge */}
            <div className="flex items-center gap-3">
              <div className="h-4 w-24 rounded skeleton-shimmer" />
              <div className="h-6 w-20 rounded-full skeleton-shimmer" />
            </div>

            {/* Resumen físico — 4 items icono + 2 líneas */}
            <div className="space-y-2">
              <div className="h-5 w-1/3 rounded skeleton-shimmer" />
              <div className="h-4 w-3/5 rounded skeleton-shimmer" />
            </div>
            <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="flex items-start gap-3 border-b border-slate-100 pb-3 dark:border-zinc-900">
                  <div className="size-8 rounded-full skeleton-shimmer shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 w-1/3 rounded skeleton-shimmer" />
                    <div className="h-4 w-1/2 rounded skeleton-shimmer" />
                  </div>
                </div>
              ))}
            </div>

            {/* Descripción — anchos variados, nunca w-full uniforme */}
            <div className="space-y-2">
              <div className="h-3 w-full rounded skeleton-shimmer" />
              <div className="h-3 w-11/12 rounded skeleton-shimmer" />
              <div className="h-3 w-4/5 rounded skeleton-shimmer" />
              <div className="h-3 w-1/2 rounded skeleton-shimmer" />
            </div>

            {/* Estacionamiento */}
            <div className="space-y-3 border-b border-slate-100 pb-6 dark:border-zinc-900">
              <div className="h-5 w-1/3 rounded skeleton-shimmer" />
              <div className="h-4 w-3/5 rounded skeleton-shimmer" />
              <div className="flex flex-wrap gap-2">
                <div className="h-6 w-20 rounded-full skeleton-shimmer" />
                <div className="h-6 w-24 rounded-full skeleton-shimmer" />
                <div className="h-6 w-16 rounded-full skeleton-shimmer" />
              </div>
              <div className="flex gap-2">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="size-10 rounded-full skeleton-shimmer" />
                ))}
              </div>
            </div>

            {/* Feature groups: Características internas + Amenidades */}
            {[0, 1].map((g) => (
              <div key={g} className="space-y-3">
                <div className="h-5 w-2/5 rounded skeleton-shimmer" />
                <div className="h-4 w-3/5 rounded skeleton-shimmer" />
                <div className="flex flex-wrap gap-2.5">
                  {["w-24", "w-20", "w-28", "w-16", "w-22", "w-20"].map((w, i) => (
                    <div key={i} className={`h-8 ${w} rounded-full skeleton-shimmer`} />
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* ── Columna derecha: collage 15 imágenes ── */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {/* Ciclo de 6 posiciones × 2 + 3 extras = 15 imágenes */}
              {([
                "md:col-span-3 h-[400px]",
                "md:col-span-2 h-[300px]",
                "md:col-span-1 h-[300px]",
                "md:col-span-1 h-[250px]",
                "md:col-span-1 h-[250px]",
                "md:col-span-1 h-[250px]",
                "md:col-span-3 h-[400px]",
                "md:col-span-2 h-[300px]",
                "md:col-span-1 h-[300px]",
                "md:col-span-1 h-[250px]",
                "md:col-span-1 h-[250px]",
                "md:col-span-1 h-[250px]",
                "md:col-span-3 h-[400px]",
                "md:col-span-2 h-[300px]",
                "md:col-span-1 h-[300px]",
              ] as const).map((spanClass, i) => (
                <div key={i} className={`rounded-2xl skeleton-shimmer ${spanClass}`} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Fallback genérico — Video, Agendamiento, Analítica, Financiera
export function GenericTabLoadingState() {
  return (
    <div className="space-y-6">
      <div className="h-[360px] rounded-[2rem] skeleton-shimmer" />
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <div className="h-24 rounded-[1.25rem] skeleton-shimmer" />
        <div className="h-24 rounded-[1.25rem] skeleton-shimmer" />
        <div className="h-24 rounded-[1.25rem] skeleton-shimmer" />
      </div>
    </div>
  );
}

// Fallback específico para el tab Ubicación — bloque de mapa
export function MapTabLoadingState() {
  return (
    <div className="space-y-4">
      <div className="relative w-full h-[480px] rounded-xl overflow-hidden">
        <div className="absolute inset-0 skeleton-shimmer" />
        {/* UI flotante: barra de dirección sobre el mapa */}
        <div className="absolute top-4 left-4 right-4 md:right-auto md:w-80 h-12 rounded-lg bg-white/80 dark:bg-black/60 shadow-sm flex items-center px-3">
          <div className="h-3 w-2/3 rounded skeleton-shimmer" />
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="h-20 rounded-[1.25rem] skeleton-shimmer" />
        <div className="h-20 rounded-[1.25rem] skeleton-shimmer" />
      </div>
    </div>
  );
}
